/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable react/require-default-props */
import {
  AutocompleteChangeReason,
  AutocompleteGetTagProps,
} from '@material-ui/lab/Autocomplete';
import { useCallback, useEffect, useState } from 'react';
import { User } from '../../types';
import DynamicAutocomplete, {
  DynamicAutocompleteType,
} from '../CustomMaterialUI/DynamicAutocomplete';

import BackendProxy from '../../logic/backendproxy';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UserValue = User | { id?: number; [key: string]: any };

export type AgentChangeEventHandler<Multiple extends boolean = false> = (
  // eslint-disable-next-line @typescript-eslint/ban-types
  event: React.ChangeEvent<{}> | null,
  value: Multiple extends true ? User[] : User | null,
  reason: AutocompleteChangeReason
) => void;

export type AgentSelectProps<
  Multiple extends boolean,
  DisableClearable extends boolean = boolean,
  FreeSolo extends boolean = boolean
> = Omit<
  DynamicAutocompleteType<UserValue, Multiple, DisableClearable, FreeSolo>,
  'options' | 'getOptionLabel' | 'filterOptions'
> & {
  filterOptions?: (option: User) => boolean;
  disableInactiveOptions?: boolean;
  extraOptions?: {
    fullName?: string;
    label?: string;
    id?: number | string;
    [key: string]: unknown;
  }[];
  error?: boolean;
  withStatus?: boolean;
  renderTags?(
    value: User[],
    getTagProps: AutocompleteGetTagProps
  ): React.ReactNode;
  setInitialValue?: boolean;
};
type AuditAgents = {
  rootBotId: number;
  userLoginStates: useLoginState[];
};

type useLoginState = {
  user: User;
  loginStatus: boolean;
};

function AgentSelectComponent<
  Multiple extends boolean,
  DisableClearable extends boolean = false,
  FreeSolo extends boolean = false
>({
  className,
  label,
  placeholder,
  disabled,
  multiple,
  value,
  onChange,
  onBlur,
  filterOptions,
  disableInactiveOptions,
  extraOptions,
  fullWidth,
  renderTags,
  withStatus = true,
  setInitialValue = false,
  getOptionSelected,
  getOptionDisabled,
  ...selectProps
}: AgentSelectProps<Multiple, DisableClearable, FreeSolo>): JSX.Element {
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    if (users.length === 0) {
      BackendProxy.getAuditFilterParams().then(
        ({ userLoginStates }: AuditAgents) => {
          const mappedUsers: User[] = userLoginStates.map(
            ({ user, loginStatus }) => {
              user.online = loginStatus;
              return user;
            }
          );

          const sortedUsers = mappedUsers.sort((a, b) => {
            if (a.online !== b.online && withStatus) {
              return a.online ? -1 : 1;
            }
            return a.fullName.toLocaleLowerCase() >
              b.fullName.toLocaleLowerCase()
              ? 1
              : -1;
          });

          setUsers(sortedUsers);
        }
      );
    }
  }, [users.length, withStatus]);

  const getFilterOptions = useCallback(
    () =>
      [...((extraOptions as User[]) ?? []), ...users].filter(u =>
        filterOptions ? filterOptions(u) : true
      ),
    [extraOptions, users, filterOptions]
  );

  const [options, setOptions] = useState<User[]>(getFilterOptions());

  useEffect(() => {
    const filteredOptions = getFilterOptions();
    setOptions(filteredOptions);
  }, [getFilterOptions, value]);

  const getOptionLabel = useCallback(
    (option: UserValue) =>
      option?.fullName ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (option as Record<string, any>)?.label ||
      options.find(agent => agent?.id === option?.id)?.fullName ||
      '',
    [options]
  );

  const getDefaultOptionSelected = <T extends UserValue>(
    option: T,
    value_?: T
  ) => option?.id === value_?.id;

  const getOptionStatus = (user: UserValue) => {
    if (!withStatus) return undefined;
    return user.online;
  };

  useEffect(() => {
    if (
      options?.length > 0 &&
      !Array.isArray(value) &&
      extraOptions?.length === 1 &&
      typeof (value as UserValue)?.id === 'undefined'
    ) {
      onChange?.(null, options[0] as any, 'select-option');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!setInitialValue) return;
    if (
      !Array.isArray(value) &&
      (value as UserValue)?.id &&
      options?.length > 0
    ) {
      const agent = options.find(
        item => item.id === Number((value as UserValue).id)
      );
      onChange?.(null, (agent || options[0]) as any, 'select-option');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, setInitialValue]);

  return (
    <DynamicAutocomplete
      className={className}
      label={label}
      placeholder={placeholder}
      multiple={multiple}
      autoComplete
      autoHighlight
      fullWidth={fullWidth}
      disableCloseOnSelect={multiple}
      size="small"
      options={options}
      renderTags={renderTags}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected ?? getDefaultOptionSelected}
      getOptionDisabled={
        disableInactiveOptions ? disabledInativeAgents : getOptionDisabled
      }
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      getOptionStatus={getOptionStatus}
      {...selectProps}
    />
  );
}

function disabledInativeAgents(option: UserValue) {
  return option?.status === 'INACTIVE';
}

export default AgentSelectComponent;
